import { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

const MultiSelectField = ({
  label,
  options = [],
  selectedValues = [],
  onChange,
  placeholder = "Select options...",
  searchPlaceholder = "Search...",
  alwaysShowOptions = [],
  disabled = false,
  keepSearchOnSelect = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  const allAvailableOptions = [...new Set([
    ...options,
    ...alwaysShowOptions
  ])];

  const searchFilteredOptions = allAvailableOptions.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredOptions = searchFilteredOptions.sort((a, b) => {
    const aSelected = selectedValues.includes(a);
    const bSelected = selectedValues.includes(b);

    if (aSelected && !bSelected) return -1;
    if (!aSelected && bSelected) return 1;
    return a.localeCompare(b);
  });

  const handleToggleOption = (option) => {
    if (disabled) return;

    const newSelected = selectedValues.includes(option)
      ? selectedValues.filter(item => item !== option)
      : [...selectedValues, option];
    onChange(newSelected);

    if (!keepSearchOnSelect) {
      setSearchTerm('');
    }
  };

  const removeTag = (optionToRemove) => {
    if (disabled) return;

    const newSelected = selectedValues.filter(item => item !== optionToRemove);
    onChange(newSelected);
  };

  return (
    <div className={`multi-select-field ${disabled ? 'disabled' : ''}`} ref={containerRef}>
      <label>{label}:</label>
      <div className="multi-select-container">
        <div className="multi-select-input-wrapper">
          {selectedValues.length > 0 && (
            <div className="selected-tags">
              {selectedValues.map(value => (
                <span key={value} className="selected-tag">
                  {value}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeTag(value);
                    }}
                    className="tag-remove"
                    disabled={disabled}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          <input
            type="text"
            className="multi-select-search-input"
            placeholder={selectedValues.length === 0 ? placeholder : searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={() => !disabled && setIsOpen(!isOpen)}
            onKeyDown={(e) => {
              if (disabled) return;

              if (e.key === 'Enter') {
                e.preventDefault();
                if (filteredOptions.length > 0) {
                  handleToggleOption(filteredOptions[0]);
                }
              } else if (e.key === 'Escape') {
                setIsOpen(false);
                setSearchTerm('');
              }
            }}
            disabled={disabled}
          />
          <FontAwesomeIcon
            icon={faChevronDown}
            className={`dropdown-arrow ${isOpen ? 'open' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) setIsOpen(!isOpen);
            }}
          />
        </div>

        {isOpen && !disabled && (
          <div className="multi-select-dropdown">
            <div className="options-list">
              {filteredOptions.length === 0 ? (
                <div className="no-options">No options found</div>
              ) : (
                filteredOptions.map(option => (
                  <div
                    key={option}
                    className={`option-item ${selectedValues.includes(option) ? 'selected' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleOption(option);
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedValues.includes(option)}
                      onChange={() => { }}
                      className="option-checkbox"
                    />
                    <span className="option-text">{option}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelectField;